import styled from '@emotion/styled';
import { useEditorState } from '@tiptap/react';
import PropTypes from 'prop-types';
import React from 'react';
import { Button } from 'react-bootstrap';
import { BsParagraph } from 'react-icons/bs';
import {
    FaBold,
    FaHighlighter,
    FaItalic,
    FaListOl,
    FaListUl,
    FaStrikethrough,
    FaSubscript,
    FaSuperscript,
    FaTable,
    FaUnderline,
} from 'react-icons/fa';
import { LuHeading1, LuHeading2, LuHeading3, LuHeading4, LuHeading5, LuHeading6 } from 'react-icons/lu';

import InsertLinkDropdown from './InsertLinkDropdown';

const ControlGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1.15em;
    border-bottom: 1px solid var(--dove);
    padding: 5px 25px;
`;

const ButtonGroup = styled.div`
    display: inline-flex;
    flex-wrap: wrap;
    gap: 3px;

    button {
        &.is-active {
            border-color: var(--dusty);
        }
    }
`;

const MenuBar = ({ editor }) => {
    const editorState = useEditorState({
        editor,
        selector: (ctx) => ({
            isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
            isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
            isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
            isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
            isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
            isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
            isParagraph: !ctx.editor.isActive('heading'),

            isBold: ctx.editor.isActive('bold') ?? false,
            isItalic: ctx.editor.isActive('italic') ?? false,
            isUnderline: ctx.editor.isActive('underline') ?? false,
            isStrike: ctx.editor.isActive('strike') ?? false,
            isSuperscript: ctx.editor.isActive('superscript') ?? false,
            isSubscript: ctx.editor.isActive('subscript') ?? false,
            isHighlight: ctx.editor.isActive('highlight') ?? false,

            isBulletList: ctx.editor.isActive('bulletList') ?? false,
            isOrderedList: ctx.editor.isActive('orderedList') ?? false,
            isLink: ctx.editor.isActive('link') ?? false,
        }),
    });

    return (
        <ControlGroup>
            <ButtonGroup>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    disabled={!editor.isEditable}
                    className={editorState.isHeading1 ? 'is-active' : ''}
                    aria-label="Heading 1"
                >
                    <LuHeading1 />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    disabled={!editor.isEditable}
                    className={editorState.isHeading2 ? 'is-active' : ''}
                    aria-label="Heading 2"
                >
                    <LuHeading2 />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    disabled={!editor.isEditable}
                    className={editorState.isHeading3 ? 'is-active' : ''}
                    aria-label="Heading 3"
                >
                    <LuHeading3 />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
                    disabled={!editor.isEditable}
                    className={editorState.isHeading4 ? 'is-active' : ''}
                    aria-label="Heading 4"
                >
                    <LuHeading4 />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
                    disabled={!editor.isEditable}
                    className={editorState.isHeading5 ? 'is-active' : ''}
                    aria-label="Heading 5"
                >
                    <LuHeading5 />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
                    disabled={!editor.isEditable}
                    className={editorState.isHeading6 ? 'is-active' : ''}
                    aria-label="Heading 6"
                >
                    <LuHeading6 />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().setParagraph().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isParagraph ? 'is-active' : ''}
                    aria-label="Paragraph"
                >
                    <BsParagraph />
                </Button>

                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isBold ? 'is-active' : ''}
                    aria-label="Bold"
                >
                    <FaBold />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isItalic ? 'is-active' : ''}
                    aria-label="Italic"
                >
                    <FaItalic />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isUnderline ? 'is-active' : ''}
                    aria-label="Underline"
                >
                    <FaUnderline />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isStrike ? 'is-active' : ''}
                    aria-label="Strikethrough"
                >
                    <FaStrikethrough />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleSuperscript().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isSuperscript ? 'is-active' : ''}
                    aria-label="Superscript"
                >
                    <FaSuperscript />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleSubscript().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isSubscript ? 'is-active' : ''}
                    aria-label="Subscript"
                >
                    <FaSubscript />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleHighlight().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isHighlight ? 'is-active' : ''}
                    aria-label="Highlight"
                >
                    <FaHighlighter />
                </Button>
            </ButtonGroup>

            <ButtonGroup>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isBulletList ? 'is-active' : ''}
                    aria-label="Bulleted list"
                >
                    <FaListUl />
                </Button>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    disabled={!editor.isEditable}
                    className={editorState.isOrderedList ? 'is-active' : ''}
                    aria-label="Numbered list"
                >
                    <FaListOl />
                </Button>
            </ButtonGroup>

            <ButtonGroup>
                <Button
                    size="xs"
                    onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                    disabled={!editor.isEditable}
                    aria-label="Insert table"
                >
                    <FaTable />
                </Button>
                <InsertLinkDropdown editor={editor} />
            </ButtonGroup>
        </ControlGroup>
    );
};

MenuBar.propTypes = {
    editor: PropTypes.shape({
        chain: PropTypes.func,
        isEditable: PropTypes.bool,
    }).isRequired,
};

export default MenuBar;
